/**
 * @fileoverview
 * Utility functions for route configuration. Provides tools for computing
 * fully resolved paths for nested route structures and for generating route paths
 * with dynamic parameters.
 */
import { generatePath } from "react-router-dom";
import type { RouteDefinition, RouteParams } from "./types";

/**
 * Recursively adds a `fullPath` property to each route in a route configuration object.
 * The `fullPath` is constructed by prepending the parent path to each route's `path`.
 *
 * @param config - The route configuration object. Each key represents a route and maps to an object
 *   that may contain a `path` and nested `children`.
 * @param parentPath - The base path to prepend to each route's `path`. Defaults to an empty string.
 * @returns The modified route configuration object with `fullPath` added to each route.
 *
 * @example
 * const routeConfig = {
 *   home: { path: 'home' },
 *   settings: {
 *     path: 'settings',
 *     children: {
 *       profile: { path: 'profile' }
 *     }
 *   }
 * };
 * addFullPathToConfig(routeConfig);
 * // Results in:
 * // {
 * //   home: { path: 'home', fullPath: '/home' },
 * //   settings: {
 * //     path: 'settings',
 * //     fullPath: '/settings',
 * //     children: {
 * //       profile: { path: 'profile', fullPath: '/settings/profile' }
 * //     }
 * //   }
 * // }
 */
export const addFullPathToConfig = (
    config: Record<string, RouteDefinition>,
    parentPath: string = ""
): Record<string, RouteDefinition> => {
    for (const route in config) {
        const { path, children } = config[route];
        const separator = path?.startsWith("/") ? "" : "/";
        const fullPath = path ? `${parentPath.replace(/\/$/, "")}${separator}${path}` : parentPath;
        config[route].fullPath = fullPath;
        if (children) {
            config[route].children = addFullPathToConfig(children, fullPath);
        }
    }
    return config;
};

/**
 * Resolves the final path for a route using its `fullPath` and parameters.
 *
 * @param config - A route object with `fullPath`.
 * @param params - Route parameters to interpolate into the path.
 * @returns A fully resolved path string with parameters substituted.
 * @throws {Error} If no valid fullPath is found in the route config or if any required parameter is missing.
 */
export const getGeneratedPathForRoute = (
    config: RouteDefinition,
    params: RouteParams = {}
): string => {
    if (!config?.fullPath || typeof config.fullPath !== "string") {
        throw new Error("No path found");
    }

    const missingParams: string[] = [];
    const pathTemplate = config.fullPath;

    // Match all parameters like ":param" or ":param?"
    const paramMatches = [...pathTemplate.matchAll(/:(\w+)\??/g)];

    for (const match of paramMatches) {
        const [fullMatch, key] = match;
        const isOptional = fullMatch.endsWith("?");

        if (!isOptional && !(key in params)) {
            missingParams.push(key);
        }
    }

    if (missingParams.length > 0) {
        throw new Error(
            `Missing required parameters for path(${pathTemplate}): ${missingParams.join(", ")}`
        );
    }

    return generatePath(pathTemplate, params);
};
